import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EmptyState } from "@/components/common";
import { fmtUSDStrict, fmtNum, timeAgo } from "@/lib/format";
import {
  Landmark, RefreshCw, ArrowUpRight, ArrowDownRight, Globe2, Clock, CircleDot, Star, ShieldCheck, Timer,
} from "lucide-react";

// "2h 14m" style countdown to an ISO instant (or null when already open/past).
function countdownTo(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "opening…";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

const STATUS_BADGE = {
  filled: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  working: "bg-[color:var(--tharwy-lime)]/15 text-[color:var(--tharwy-lime-700)] border-[color:var(--tharwy-lime)]/30",
  cancelled: "bg-muted text-muted-foreground border-border",
};

// Positions / Orders / Queued / Fills / Markets blotter ("order history").
export const BrokerBlotter = ({ b }) => {
  const { positions, orders, fills, markets, queuedOrders, openOrders, marketByCode, nowTick, cancel, sync, openExchange } = b;
  // nowTick is referenced so countdowns re-render each second.
  void nowTick;

  return (
    <div className="lg:col-span-2">
      <Tabs defaultValue="positions">
        <div className="flex items-center justify-between mb-3">
          <TabsList data-testid="broker-tabs">
            <TabsTrigger value="positions" data-testid="broker-tab-positions">Positions</TabsTrigger>
            <TabsTrigger value="orders" data-testid="broker-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="queued" data-testid="broker-tab-queued">
              Queued{queuedOrders.length ? ` (${queuedOrders.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="fills" data-testid="broker-tab-fills">Fills</TabsTrigger>
            <TabsTrigger value="markets" data-testid="broker-tab-markets">Markets</TabsTrigger>
          </TabsList>
          {openOrders.length > 0 && (
            <Button size="sm" variant="secondary" className="gap-1.5" onClick={sync} data-testid="broker-sync">
              <RefreshCw className="h-3.5 w-3.5" /> Sync fills
            </Button>
          )}
        </div>

        <TabsContent value="positions">
          <Card className="p-4 shadow-premium">
            {positions.length === 0 ? (
              <EmptyState title="No positions" hint="Route an order to open a position." testid="broker-positions-empty" />
            ) : (
              <Table data-testid="broker-positions-table">
                <TableHeader><TableRow><TableHead>Symbol</TableHead><TableHead className="text-end">Qty</TableHead><TableHead className="text-end">Avg</TableHead><TableHead className="text-end">Last</TableHead><TableHead className="text-end">Value</TableHead><TableHead className="text-end">P&L</TableHead></TableRow></TableHeader>
                <TableBody>{positions.map((p, i) => (
                  <TableRow key={`${p.symbol}-${i}`} data-testid={`broker-position-${p.symbol}`}>
                    <TableCell><div className="font-medium">{p.symbol.split(".")[0]}</div><div className="text-xs text-muted-foreground truncate max-w-[150px]">{p.name}</div></TableCell>
                    <TableCell className="text-end numeric">{fmtNum(p.qty, p.qty % 1 ? 4 : 0)}</TableCell>
                    <TableCell className="text-end numeric">{fmtNum(p.avg_cost)}</TableCell>
                    <TableCell className="text-end numeric">{fmtNum(p.last_price)}</TableCell>
                    <TableCell className="text-end numeric">{fmtUSDStrict(p.market_value_usd)}</TableCell>
                    <TableCell className={`text-end numeric ${p.pnl_usd >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      <span className="inline-flex items-center gap-0.5">{p.pnl_usd >= 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}{p.pnl_pct}%</span>
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="orders">
          <Card className="p-4 shadow-premium">
            {orders.length === 0 ? (
              <EmptyState title="No orders" hint="Submitted orders appear here." testid="broker-orders-empty" />
            ) : (
              <Table data-testid="broker-orders-table">
                <TableHeader><TableRow><TableHead>Symbol</TableHead><TableHead>Side</TableHead><TableHead>Type</TableHead><TableHead className="text-end">Qty</TableHead><TableHead className="text-end">Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{orders.map((o) => (
                  <TableRow key={o.id} data-testid={`broker-order-${o.id}`}>
                    <TableCell className="font-medium">{o.symbol.split(".")[0]}</TableCell>
                    <TableCell className={o.side === "buy" ? "text-emerald-600 capitalize" : "text-red-600 capitalize"}>{o.side}</TableCell>
                    <TableCell className="capitalize text-muted-foreground">{o.order_type}{o.limit_price ? ` @ ${fmtNum(o.limit_price)}` : ""}</TableCell>
                    <TableCell className="text-end numeric">{fmtNum(o.qty, o.qty % 1 ? 4 : 0)}</TableCell>
                    <TableCell className="text-end"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border capitalize ${o.queued && o.status === "working" ? "bg-amber-500/15 text-amber-600 border-amber-500/30" : (STATUS_BADGE[o.status] || STATUS_BADGE.working)}`}>{o.queued && o.status === "working" ? "queued" : o.status}</span></TableCell>
                    <TableCell className="text-end">{o.status === "working" && <Button size="sm" variant="ghost" onClick={() => cancel(o.id)} data-testid={`broker-cancel-${o.id}`}>Cancel</Button>}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="fills">
          <Card className="p-4 shadow-premium">
            {fills.length === 0 ? (
              <EmptyState title="No fills yet" hint="Executions appear here." testid="broker-fills-empty" />
            ) : (
              <Table data-testid="broker-fills-table">
                <TableHeader><TableRow><TableHead>Symbol</TableHead><TableHead>Side</TableHead><TableHead className="text-end">Qty</TableHead><TableHead className="text-end">Price</TableHead><TableHead className="text-end">Value</TableHead><TableHead className="text-end">Time</TableHead></TableRow></TableHeader>
                <TableBody>{fills.map((f) => (
                  <TableRow key={f.id} data-testid={`broker-fill-${f.id}`}>
                    <TableCell className="font-medium">{f.symbol.split(".")[0]}</TableCell>
                    <TableCell className={f.side === "buy" ? "text-emerald-600 capitalize" : "text-red-600 capitalize"}>{f.side}</TableCell>
                    <TableCell className="text-end numeric">{fmtNum(f.qty, f.qty % 1 ? 4 : 0)}</TableCell>
                    <TableCell className="text-end numeric">{fmtNum(f.fill_price)}</TableCell>
                    <TableCell className="text-end numeric">{fmtUSDStrict(f.cost_usd)}</TableCell>
                    <TableCell className="text-end text-xs text-muted-foreground">{timeAgo(f.timestamp)}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="queued">
          <Card className="p-4 shadow-premium" data-testid="broker-queued-panel">
            {queuedOrders.length === 0 ? (
              <EmptyState title="No queued orders" hint="Orders placed while a market is closed queue here and fill at the next open." testid="broker-queued-empty" />
            ) : (
              <div className="space-y-2.5" data-testid="broker-queued-list">
                {queuedOrders.map((o) => {
                  const mk = marketByCode[o.market];
                  const cd = countdownTo(mk?.next_open_utc);
                  const buy = o.side === "buy";
                  return (
                    <div key={o.id} className="rounded-lg border p-3 flex items-center gap-3" data-testid={`broker-queued-${o.id}`}>
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${buy ? "bg-emerald-500/15" : "bg-red-500/15"}`}>
                        {buy ? <ArrowUpRight className="h-4 w-4 text-emerald-600" /> : <ArrowDownRight className="h-4 w-4 text-red-600" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{o.symbol}</span>
                          <Badge variant="secondary" className="text-[10px] uppercase">{o.side}</Badge>
                          <span className="text-xs text-muted-foreground">{o.qty} @ {o.order_type}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Globe2 className="h-3 w-3" />{mk?.name || o.market} · {mk?.next_event || "opens next session"}
                        </div>
                      </div>
                      <div className="text-end shrink-0">
                        <div className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--tharwy-lime-700)] numeric" data-testid={`broker-queued-countdown-${o.id}`}>
                          <Timer className="h-3.5 w-3.5" />{cd || "—"}
                        </div>
                        <div className="text-[10px] text-muted-foreground">to open</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => cancel(o.id)} data-testid={`broker-queued-cancel-${o.id}`}>Cancel</Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="markets">
          <Card className="p-4 shadow-premium" data-testid="broker-markets-panel">
            {!markets ? (
              <EmptyState title="Loading global markets…" />
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[["Exchanges", markets.totals.exchanges], ["Open now", markets.totals.open],
                    ["Instruments", markets.totals.instruments.toLocaleString()], ["Currencies", markets.totals.currencies]].map(([l, v]) => (
                    <div key={l} className="rounded-lg border p-3" data-testid={`broker-markets-stat-${l}`}>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div>
                      <div className="numeric text-xl font-semibold">{v}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mb-4 flex items-start gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600" />
                  Global stocks &amp; ETFs routable from your account. Orders settle from your USD balance at the live FX rate. Live routing activates once the market gateway is connected.
                </p>
                <div className="space-y-5">
                  {markets.regions.map((r) => (
                    <div key={r.region} data-testid={`broker-region-${r.region}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Globe2 className="h-4 w-4 text-[color:var(--tharwy-lime-700)]" />
                        <h3 className="text-sm font-semibold">{r.region}</h3>
                        <Badge variant="secondary" className="text-[10px]">{r.exchanges.length}</Badge>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {r.exchanges.map((ex) => (
                          <button type="button" key={ex.code} onClick={() => openExchange(ex)}
                            className="text-start rounded-lg border p-3 flex items-center justify-between gap-2 hover:bg-accent hover:border-[color:var(--tharwy-lime)]/40 transition-colors"
                            data-testid={`broker-exchange-${ex.code}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate flex items-center gap-1.5">{ex.name}<Star className="h-3 w-3 text-muted-foreground/50" /></div>
                              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                                <span>{ex.currency}</span>·<span>{ex.instruments} listings</span>
                                {ex.tz_label ? <><span>·</span><span className="hidden sm:inline">{ex.tz_label}</span></> : null}
                              </div>
                            </div>
                            <div className="text-end shrink-0">
                              <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${ex.is_open ? "text-emerald-600" : "text-muted-foreground"}`}>
                                <CircleDot className="h-3 w-3" />{ex.is_open ? "Open" : "Closed"}
                              </span>
                              <div className="text-[10px] text-muted-foreground flex items-center gap-1 justify-end mt-0.5">
                                <Clock className="h-3 w-3" />{ex.local_time || ex.hours}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
